import { useCallback, useEffect, useRef, useState } from 'react'

import { DragContext, setDragContext } from '../stores/dragStore'
import { useDragGhost } from './useDragGhost'
import { useTouchDragInitiation } from './useTouchDragInitiation'

type TouchDragWithGhostOptions<T extends DragContext> = {
  element: HTMLElement
  dragContext: T
  ghostOptions: {
    width: number
    height: number
  }
  onDragStart?: (position: { clientX: number; clientY: number }) => void
  onDragEnd?: (position: { clientX: number; clientY: number }) => void
  enabled?: boolean
  getCurrentDragData?: () => {
    element?: HTMLElement
    dragContext?: T
    ghostOptions?: { width: number; height: number }
  } | null
}

export const useTouchDragWithGhost = <T extends DragContext>({
  element,
  dragContext,
  ghostOptions,
  onDragStart,
  onDragEnd,
  enabled = true,
  getCurrentDragData
}: TouchDragWithGhostOptions<T>) => {
  const { createDragGhost, updateDragGhostPosition, removeDragGhost } =
    useDragGhost()
  const touchMoveHandlerRef = useRef<((e: TouchEvent) => void) | null>(null)
  const touchEndHandlerRef = useRef<((e: TouchEvent) => void) | null>(null)
  const isDraggingRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)

  const cleanup = useCallback(() => {
    if (touchMoveHandlerRef.current) {
      document.removeEventListener('touchmove', touchMoveHandlerRef.current)
      touchMoveHandlerRef.current = null
    }
    if (touchEndHandlerRef.current) {
      document.removeEventListener('touchend', touchEndHandlerRef.current)
      document.removeEventListener('touchcancel', touchEndHandlerRef.current)
      touchEndHandlerRef.current = null
    }
    isDraggingRef.current = false
    setIsDragging(false)
    removeDragGhost()
  }, [removeDragGhost])

  const startDrag = useCallback(
    (touchPosition: { x: number; y: number }) => {
      if (!enabled) return

      onDragStart?.({ clientX: touchPosition.x, clientY: touchPosition.y })
      const currentDragData = getCurrentDragData?.()
      const currentElement = currentDragData?.element ?? element
      const currentDragContext =
        (currentDragData?.dragContext as T) ?? dragContext
      const currentGhostOptions = currentDragData?.ghostOptions ?? ghostOptions

      setDragContext(currentDragContext)

      isDraggingRef.current = true
      setIsDragging(true)

      // create drag ghost at the touch position
      const offsetX = currentGhostOptions.width / 2
      const offsetY = currentGhostOptions.height / 2
      createDragGhost(
        currentElement,
        { ...currentGhostOptions, offsetX, offsetY },
        touchPosition
      )

      requestAnimationFrame(() => {
        updateDragGhostPosition(
          touchPosition.x,
          touchPosition.y,
          offsetX,
          offsetY
        )
      })

      // Add global touch handlers
      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault()
        const touch = e.touches[0]
        if (!touch) return

        updateDragGhostPosition(touch.clientX, touch.clientY, offsetX, offsetY)
      }

      const handleTouchEnd = (e: TouchEvent) => {
        e.preventDefault()
        const touch = e.changedTouches[0]
        cleanup()

        requestAnimationFrame(() => {
          if (onDragEnd && touch) {
            onDragEnd({
              clientX: touch.clientX,
              clientY: touch.clientY
            })
          }
          setDragContext(null)
        })
      }

      touchMoveHandlerRef.current = handleTouchMove
      touchEndHandlerRef.current = handleTouchEnd

      document.addEventListener('touchmove', handleTouchMove, {
        passive: false
      })
      document.addEventListener('touchend', handleTouchEnd, { passive: false })
      document.addEventListener('touchcancel', handleTouchEnd, {
        passive: false
      })
    },
    [
      enabled,
      onDragStart,
      getCurrentDragData,
      element,
      dragContext,
      ghostOptions,
      createDragGhost,
      updateDragGhostPosition,
      cleanup,
      onDragEnd
    ]
  )

  const {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    clearTouchTimeout
  } = useTouchDragInitiation({
    onDragStart: startDrag,
    enabled: true // always enabled, we check inside startDrag
  })

  useEffect(() => {
    return () => {
      clearTouchTimeout()
      cleanup()
    }
  }, [clearTouchTimeout, cleanup])

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isDragging
  }
}
